import React, {ReactElement, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useSlideErrors} from '../../../../context/SlideErrorsContext';
import {IValidationErrorObject} from '../../../../incase-domain/common/validationErrorResponse';
import {ELEMENT} from '../../../../incase-domain/elements/base/ElementTypes';
import {setHighlightedElementTabAction} from '../../../../store/actions/grid';
import {highlightedElementTabSelector} from '../../../../store/selectors/grid';
import Tabs from '../../../../ui/components/tabs/Tabs';
import HighlightedButtonElementComponent from '../../highlighted/button';
import ElementsToDisplayBeforeAfter from '../../highlighted/common/elementsToClickBeforeAfter/elementsToDisplayBeforeAfter';
import {ElementGamificationOptions} from '../../highlighted/common/gamification';
import HideOnMobile from '../../highlighted/common/hideOnMobile';
import HighlightedFlipCardElementComponent from '../../highlighted/flipCard/flipCard';
import HighlightedFormElementComponent from '../../highlighted/form/form';
import HighlightedGeometryElementComponent from '../../highlighted/geometry/geometry';
import HighlightedLineElementComponent from '../../highlighted/geometry/line';
import HighlightedHtmlElementComponent from '../../highlighted/html/html';
import HighlightedTextInputElementComponent from '../../highlighted/input/textInput';
import HighlightedImageElementComponent from '../../highlighted/media/image';
import HighlightedImageGalleryElementComponent from '../../highlighted/media/imageGallery/imageGallery';
import HighlightedVideoElementComponent from '../../highlighted/media/video';
import HighlightedModalElementComponent from '../../highlighted/modal/modal';
import HighlightedImageMarkupElementComponent from '../../highlighted/question/imageMarkup.question';
import HighlightedLikertScaleQuestionComponent from '../../highlighted/question/likertScale.question';
import HighlightedQuestionMatchPairElementComponent from '../../highlighted/question/matchPair.question';
import HighlightedMultiSelectElementComponent from '../../highlighted/question/multiselect';
import HighlightedPhotoAnswerQuestionElementComponent from '../../highlighted/question/photoAnswer.question';
import HighlightedQuestionElementComponent from '../../highlighted/question/question';
import HighlightedElementSequenceQuestionComponent from '../../highlighted/question/sequence/sequence.question';
import HighlightedYesNoQuestionElementComponent from '../../highlighted/question/yesnoDeprecated/yesno.question';
import HighlightedQuestionReportElementComponent from '../../highlighted/questionReport/questionReport';
import HighlightedStaticListElementComponent from '../../highlighted/staticList';
import HighlightedStaticTableElementComponent from '../../highlighted/table/v1/staticTable';
import HighlightedStaticTableV2ElementComponent from '../../highlighted/table/v2/staticTableV2';
import HighlightedStaticTextElementComponent from '../../highlighted/text/staticText';
import {ELEMENT_TABS, TABS} from './tabs';
import HighlightedCalculatorElementComponent from '../../highlighted/calculator/HighlightedCalculatorElementComponent';
import HighlightedQuestionYesNoV2ElementComponent from '../../highlighted/question/yesnoV2.question';
import {useHighlightedElement} from '../../../../utils/hooks/slideUpdates/useSetSlideElement';
import HighlightedLineElementComponentV2 from '../../highlighted/geometry/lineV2';
import HighlightedChatbotElementComponent from '../../highlighted/chatbot/HighlightedChatbotElementComponent';

function HighlightedElementComponent(): ReactElement {
    const dispatch = useDispatch();
    const highlightedElement = useHighlightedElement();
    const {type} = highlightedElement;
    const {errors} = useSlideErrors();
    const pendingTab = useSelector(highlightedElementTabSelector);

    const [activeTab, setActiveTab] = useState(
        (ELEMENT_TABS[highlightedElement.type] && ELEMENT_TABS[highlightedElement.type][0].id) || ''
    );

    useEffect(() => {
        if (pendingTab) {
            setActiveTab(pendingTab);
            dispatch(setHighlightedElementTabAction(null));
        }
    }, [pendingTab, dispatch]);

    const elementErrors: IValidationErrorObject | null = useMemo(() => {
        const slideErrors = errors ? (errors.errors as IValidationErrorObject) : {};
        const hasElementErrors =
            slideErrors?.elements &&
            Array.isArray(slideErrors.elements) &&
            slideErrors.elements[0] &&
            Object.keys(slideErrors.elements[0]).includes(highlightedElement.id);

        if (hasElementErrors && Array.isArray(slideErrors.elements)) {
            return slideErrors.elements[0][highlightedElement.id][0] as IValidationErrorObject;
        }

        return null;
    }, [errors, highlightedElement.id]);

    let highlightedElementComponent;
    switch (type) {
        case ELEMENT.STATIC_TEXT:
            highlightedElementComponent = <HighlightedStaticTextElementComponent activeTab={activeTab} />;
            break;
        case ELEMENT.HTML:
            highlightedElementComponent = <HighlightedHtmlElementComponent activeTab={activeTab} />;
            break;
        case ELEMENT.STATIC_LIST:
            highlightedElementComponent = <HighlightedStaticListElementComponent activeTab={activeTab} errors={elementErrors} />;
            break;
        case ELEMENT.STATIC_TABLE:
            highlightedElementComponent = <HighlightedStaticTableElementComponent activeTab={activeTab} />;
            break;
        case ELEMENT.STATIC_TABLE_V2:
            highlightedElementComponent = <HighlightedStaticTableV2ElementComponent activeTab={activeTab} />;
            break;
        case ELEMENT.IMAGE:
            highlightedElementComponent = <HighlightedImageElementComponent activeTab={activeTab} errors={elementErrors} />;
            break;
        case ELEMENT.IMAGE_GALLERY:
            highlightedElementComponent = <HighlightedImageGalleryElementComponent activeTab={activeTab} errors={elementErrors} />;
            break;
        case ELEMENT.VIDEO:
            highlightedElementComponent = <HighlightedVideoElementComponent activeTab={activeTab} errors={elementErrors} />;
            break;
        case ELEMENT.QUESTION:
            highlightedElementComponent = <HighlightedQuestionElementComponent activeTab={activeTab} errors={elementErrors} />;
            break;
        case ELEMENT.PHOTO_ANSWER_QUESTION:
            highlightedElementComponent = <HighlightedPhotoAnswerQuestionElementComponent activeTab={activeTab} errors={elementErrors} />;
            break;
        case ELEMENT.IMAGE_MARKUP:
            highlightedElementComponent = <HighlightedImageMarkupElementComponent activeTab={activeTab} errors={elementErrors} />;
            break;
        case ELEMENT.YES_NO_QUESTION_V2:
            highlightedElementComponent = <HighlightedQuestionYesNoV2ElementComponent activeTab={activeTab} errors={elementErrors} />;
            break;
        case ELEMENT.YES_NO_QUESTION:
            highlightedElementComponent = <HighlightedYesNoQuestionElementComponent activeTab={activeTab} errors={elementErrors} />;
            break;
        case ELEMENT.LIKERT_SCALE:
            highlightedElementComponent = <HighlightedLikertScaleQuestionComponent activeTab={activeTab} errors={elementErrors} />;
            break;
        case ELEMENT.QUESTION_REPORT:
            highlightedElementComponent = <HighlightedQuestionReportElementComponent activeTab={activeTab} errors={elementErrors} />;
            break;
        case ELEMENT.SEQUENCE_QUESTION:
            highlightedElementComponent = <HighlightedElementSequenceQuestionComponent activeTab={activeTab} errors={elementErrors} />;
            break;
        case ELEMENT.MULTI_SELECT:
            highlightedElementComponent = <HighlightedMultiSelectElementComponent activeTab={activeTab} errors={elementErrors} />;
            break;
        case ELEMENT.MATCH_PAIR_QUESTION:
            highlightedElementComponent = <HighlightedQuestionMatchPairElementComponent activeTab={activeTab} errors={elementErrors} />;
            break;
        case ELEMENT.BUTTON:
            highlightedElementComponent = <HighlightedButtonElementComponent activeTab={activeTab} errors={elementErrors} />;
            break;
        case ELEMENT.TEXT_INPUT:
            highlightedElementComponent = <HighlightedTextInputElementComponent activeTab={activeTab} errors={elementErrors} />;
            break;
        case ELEMENT.FORM:
            highlightedElementComponent = <HighlightedFormElementComponent activeTab={activeTab} errors={elementErrors} />;
            break;
        case ELEMENT.MODAL:
            highlightedElementComponent = <HighlightedModalElementComponent activeTab={activeTab} errors={elementErrors} />;
            break;
        case ELEMENT.FLIP_CARD:
            highlightedElementComponent = <HighlightedFlipCardElementComponent activeTab={activeTab} errors={elementErrors} />;
            break;
        case ELEMENT.CALCULATOR:
            highlightedElementComponent = <HighlightedCalculatorElementComponent activeTab={activeTab} />;
            break;
        case ELEMENT.CHATBOT:
            highlightedElementComponent = <HighlightedChatbotElementComponent activeTab={activeTab} errors={elementErrors} />;
            break;
        case ELEMENT.ARROW:
            highlightedElementComponent = <HighlightedGeometryElementComponent activeTab={activeTab} withFill={false} />;
            break;
        case ELEMENT.LINE:
            highlightedElementComponent = <HighlightedLineElementComponent activeTab={activeTab} />;
            break;
        case ELEMENT.LINE_V2:
            highlightedElementComponent = <HighlightedLineElementComponentV2 activeTab={activeTab} />;
            break;
        case ELEMENT.ARROW_V2:
            highlightedElementComponent = <HighlightedLineElementComponentV2 activeTab={activeTab} />;
            break;
        case ELEMENT.CIRCLE:
        case ELEMENT.ELLIPSE:
        case ELEMENT.RECTANGLE:
        case ELEMENT.TRIANGLE:
        case ELEMENT.STAR:
            highlightedElementComponent = <HighlightedGeometryElementComponent activeTab={activeTab} />;
            break;
        default:
            break;
    }

    const showElementsToDisplayBeforeAfter = useMemo(
        () =>
            [
                ELEMENT.STATIC_TEXT,
                ELEMENT.HTML,
                ELEMENT.STATIC_LIST,
                ELEMENT.STATIC_TABLE,
                ELEMENT.STATIC_TABLE_V2,
                ELEMENT.IMAGE,
                ELEMENT.IMAGE_GALLERY,
                ELEMENT.VIDEO,
                ELEMENT.QUESTION,
                ELEMENT.PHOTO_ANSWER_QUESTION,
                ELEMENT.IMAGE_MARKUP,
                ELEMENT.YES_NO_QUESTION_V2,
                ELEMENT.YES_NO_QUESTION,
                ELEMENT.QUESTION_REPORT,
                ELEMENT.MATCH_PAIR_QUESTION,
                ELEMENT.BUTTON,
                ELEMENT.TEXT_INPUT,
            ].includes(type),
        [type]
    );

    const showElementGamificationOptions = useMemo(
        () =>
            [
                ELEMENT.QUESTION,
                ELEMENT.PHOTO_ANSWER_QUESTION,
                ELEMENT.IMAGE_MARKUP,
                ELEMENT.YES_NO_QUESTION_V2,
                ELEMENT.YES_NO_QUESTION,
                ELEMENT.SEQUENCE_QUESTION,
                ELEMENT.MULTI_SELECT,
                ELEMENT.MATCH_PAIR_QUESTION,
            ].includes(type),
        [type]
    );

    if (highlightedElementComponent) {
        return (
            <>
                <Tabs tabs={ELEMENT_TABS[highlightedElement.type]} activeTab={activeTab} setActiveTab={setActiveTab} />

                {highlightedElementComponent}

                {activeTab === TABS.ADVANCED.id && (
                    <>
                        {showElementsToDisplayBeforeAfter && <ElementsToDisplayBeforeAfter />}

                        {showElementGamificationOptions && <ElementGamificationOptions />}

                        <HideOnMobile />
                    </>
                )}
            </>
        );
    }

    return <div />;
}

export default HighlightedElementComponent;
